import {registerPlugin} from '@capacitor/core';

/** Non-sensitive runtime configuration shared with the cached Flutter engine. */
export interface FlutterShellPresentOptions {
  apiUrl: string;
  locale: string;
  channelCode: string;
  flow: 'vehicle-wizard' | 'social-list' | 'media-composer';
}

export interface FlutterVehicleWizardPresentOptions extends FlutterShellPresentOptions {
  flow: 'vehicle-wizard';
}

export type FlutterSocialListType =
  | 'user-follows'
  | 'project-follows'
  | 'event-follows'
  | 'user-reactions'
  | 'project-reactions'
  | 'local-subject-reactions';

export interface FlutterSocialListPresentOptions extends FlutterShellPresentOptions {
  flow: 'social-list';
  listType: FlutterSocialListType;
  subjectIri: string;
}

export type FlutterMediaComposerStart = 'camera' | 'gallery';
export type FlutterMediaComposerMediaType = 'image' | 'video';

export interface FlutterMediaComposerPresentOptions extends FlutterShellPresentOptions {
  flow: 'media-composer';
  start: FlutterMediaComposerStart;
}

export interface FlutterMediaComposerResult {
  outcome: 'completed';
  mediaPath: string;
  mediaUrl?: string | null;
  mediaType: FlutterMediaComposerMediaType;
  mimeType: string;
  thumbnailPath?: string | null;
  thumbnailUrl?: string | null;
  thumbnailTime?: number | null;
  filterPreset?: string | null;
  caption?: string | null;
}

export type FlutterVehicleWizardOutcome = 'created' | 'cancelled';
export type FlutterVehicleWizardResult =
  | {outcome: 'cancelled'}
  | {
      outcome: 'created';
      projectJson?: string;
      vehicleIri?: string;
      vehicleIdentity?: string;
      vehicleTypeCode?: string;
    };
export type FlutterSocialListOutcome = 'closed' | 'navigated';
export interface FlutterShellDismissEvent {
  outcome: FlutterVehicleWizardOutcome | FlutterSocialListOutcome | 'completed' | 'failed';
  projectJson?: string;
  vehicleIri?: string;
  vehicleIdentity?: string;
  vehicleTypeCode?: string;
  route?: string;
  mediaPath?: string;
  mediaUrl?: string | null;
  mediaType?: FlutterMediaComposerMediaType;
  mimeType?: string;
  thumbnailPath?: string | null;
  thumbnailUrl?: string | null;
  thumbnailTime?: number | null;
  filterPreset?: string | null;
  caption?: string | null;
}

export interface FlutterShellPlugin {
  present(options: FlutterVehicleWizardPresentOptions | FlutterSocialListPresentOptions | FlutterMediaComposerPresentOptions): Promise<void>;
  dismiss(): Promise<void>;
  addListener(
    eventName: 'didDismiss',
    listenerFunc: (event: FlutterShellDismissEvent) => void,
  ): Promise<{remove: () => Promise<void>}>;
}

/** Registered only by native hosts that include the Flutter add-to-app module. */
export const FlutterShell = registerPlugin<FlutterShellPlugin>('FlutterShell');
